import { Card, CardContent, Typography, Button, Box, Chip, IconButton, ButtonGroup, Stack, Sheet, CircularProgress } from "@mui/joy";

import { Play, ChartBar, UserGear, Trash, MagnifyingGlass } from '@phosphor-icons/react';

import { useNavigate } from "react-router";
import { APIDeleteTest } from "../../api";
import { useContext, useState } from "react";
import { LayoutContext } from "../../context";
import TestResultsModal from "../modals/TestResultsModal";


export default function CardTest({ test }) {
    const navigate = useNavigate();
    const { token, loadUserTests } = useContext(LayoutContext);
    const [deleting, setDeleting] = useState(false);

    const [resultsModalOpen, setResultsModalOpen] = useState(false);


    const handleStartTest = () => {
        navigate(`/test-session/${test.id}`);
    };

    // Обновляем функцию handleViewResults:
    const handleViewResults = () => {
        setResultsModalOpen(true);
    };
    
    const handleDeleteTest = async () => {
        if (!window.confirm(`Вы уверены, что хотите удалить тест "${test.title}"?\n\nЭто действие нельзя отменить.`)) {
            return;
        }

        setDeleting(true);
        try {
            const response = await APIDeleteTest(token, test.id);
            if (response.success) {
                loadUserTests();
            } else {
                alert(response.error);
            }
        } catch (error) {
            console.error('Error deleting test:', error);
            alert('Ошибка при удалении теста');
        } finally {
            setDeleting(false);
        }
    };

    // Форматируем дату создания
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    // Получаем количество правильных ответов
    const getCorrectAnswersCount = () => {
        return test.test_questions?.filter(q => q.user_answer?.is_correct).length || 0;
    };

    const progressPercentage = test.progress_percentage || 0;
    const completedQuestions = test.completed_questions || 0;
    const totalQuestions = test.total_questions || 0;

    return (
        <>
            <Card 
                variant="outlined" 
                sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: '0.2s',
                    borderRadius: 'xl',
                    '&:hover': {
                        boxShadow: 'md',
                        borderColor: 'primary.500',
                        transform: 'translateY(-2px)'
                    }
                }}
                // onClick={handleCardClick}
            >
                <Typography level="h3" sx={{ mb: 1, fontWeight: 300 }}>
                    {test.title}
                </Typography>
                
                <CardContent sx={{ flexGrow: 1, justifyContent: 'space-between' }}>
                    <Stack>
                        {/* Категории */}
                        <Stack direction={"row"} spacing={0} flexWrap={"wrap"} sx={{ gap: 0.5, mb: 2 }}>
                            {test.selected_categories?.slice(0, 3).map((categoryItem) => (
                                <Chip 
                                    key={categoryItem.id}
                                    variant="soft" 
                                    color="primary" 
                                    size="sm"
                                >
                                    {categoryItem.category.name}
                                </Chip>
                            ))}
                            {test.selected_categories?.length > 3 && (
                                <Chip variant="soft" color="neutral" size="sm">
                                    +{test.selected_categories.length - 3}
                                </Chip>
                            )}
                        </Stack>

                        {/* Информация о тесте */}
                        <Stack spacing={0} sx={{ mb: 2 }}>
                            <Typography level="body2">
                                Режим: <strong>{test.mode === 'study' ? 'Учебный' : 'Тестовый'}</strong>
                            </Typography>
                            <Typography level="body2">
                                Вопросов: <strong>{totalQuestions}</strong>
                            </Typography>
                            <Typography level="body2">
                                Статус: <strong>{test.is_completed ? 'Завершен' : 'В процессе'}</strong>
                            </Typography>
                            <Typography level="body2" color="neutral">
                                Всего вопросов:  <strong>{test.actual_questions_count}</strong>
                            </Typography>
                            <Typography level="body2" color="neutral">
                                Прогресс: <strong>{test.completed_questions} / {test.actual_questions_count}</strong>
                            </Typography>
                            {test.is_completed && (
                                <Typography level="body2" color="neutral">
                                    Правильных ответов: <strong>{getCorrectAnswersCount()}</strong>
                                </Typography>
                            )}

                            {test.progress_percentage > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 0.5, alignItems: 'center' }}>
                                    <Typography level="body2" color="neutral">
                                        Завершено:
                                    </Typography>
                                    <Chip 
                                        size="md" 
                                        variant="soft" 
                                        color={
                                            test.progress_percentage === 100 ? 'success' : 
                                            test.progress_percentage > 50 ? 'primary' : 'neutral'
                                        }
                                    >
                                        {test.progress_percentage}%
                                    </Chip>
                                </Box>
                            )}
                        </Stack>

                        
                    </Stack>

                    {/* Прогресс и кнопка */}
                    <Stack direction={"row"} spacing={1} justifyContent={'space-between'} alignItems="center">
                        <Box sx={{ position: 'relative' }}>
                            <CircularProgress 
                                size="lg" 
                                determinate 
                                value={progressPercentage}
                                color={test.is_completed ? "success" : "primary"}
                            >
                                <Typography level="body-sm" fontWeight="bold">
                                    {progressPercentage}%
                                </Typography>
                            </CircularProgress>
                        </Box>
                        <Stack spacing={1} >
                            <Stack spacing={1} direction={'row'}>
                                {/* Кнопка удаления */}
                                <IconButton
                                    size="sm"
                                    variant="soft"
                                    color="danger"
                                    onClick={handleDeleteTest}
                                    loading={deleting}
                                    disabled={deleting}
                                    sx={{
                                        // position: 'absolute',
                                        // top: 8,
                                        // right: 8,
                                        // zIndex: 1
                                        px: 1.5
                                    }}
                                    title="Удалить тест"
                                >
                                    <Trash size={18} />
                                </IconButton>
                                
                                {!test.is_completed ? (
                                    <Button
                                        variant="solid"
                                        color="primary"
                                        startDecorator={<Play size={18} />}
                                        onClick={handleStartTest}
                                        fullWidth
                                    >
                                        {test.completed_questions > 0 ? 'Продолжить' : 'Начать'}
                                    </Button>
                                ) : (
                                    <>
                                        <Button
                                            variant="solid"
                                            color="primary"
                                            startDecorator={<MagnifyingGlass size={18} />}
                                            onClick={handleStartTest}
                                            fullWidth
                                        >
                                            Разбор
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="neutral"
                                            startDecorator={<ChartBar size={18} />}
                                            onClick={handleViewResults}
                                            fullWidth
                                        >
                                            Результаты
                                        </Button>
                                    </>
                                )}
                            </Stack>

                            {/* Дата создания */}
                            <Box sx={{ mt: 'auto' }}>
                                <Typography level="body3" color="neutral" sx={{ opacity: 0.7 }}>
                                    Создан: {formatDate(test.date_create)}
                                </Typography>
                                {test.completed_date && (
                                    <Typography level="body3" color="neutral" sx={{ opacity: 0.7 }}>
                                        Завершен: {formatDate(test.completed_date)}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            <TestResultsModal
                open={resultsModalOpen}
                test={test}
                token={token}
                onClose={() => setResultsModalOpen(false)}
            />
        </>
    );
}